import { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  AvsChangeLogs,
  Design,
  DesignChangeMap
} from '@app/types';
import {
  useDesign,
  useLatestDesign,
  useServices
} from '@app/hooks';
import { Loader } from '@platform/ui-library';
import { formatDateTime } from '@app/utilities';
import { ChangeSetWrapper, filterExcluded } from './change-set-wrapper';
import Utility from '@app/utilities/Utility';
import style from './style/change-log.module.scss';
import { VersionSelector } from '@app/page-components';
import { Label } from '@app/form_components';

type ChangeLogProps = {
  serialized?: Design;
  version?: string;
};

export function ChangeLog({ serialized }: ChangeLogProps) {
  const { avsService } = useServices();
  const { branchId } = useDesign();
  const { version: latestVersion } = useLatestDesign() ?? {};
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [changeLogs, setNewChangeLogs] = useState<AvsChangeLogs[] | null>(null);
  const changelogVersion = selectedVersion ?? latestVersion;

  useEffect(() => {
    (async () => {
      if (!changelogVersion) {
        return;
      }
      setNewChangeLogs([]);
      setNewChangeLogs(await avsService.getChangeLogs(branchId, changelogVersion));
    })();
  }, [changelogVersion]);

  return (
    <>
      <div className={style.changeLogControls}>
        <div className="controls">
          <Label tooltip='Will display Change Logs for the selected version and up to 20 previous ones'>Selected Version</Label>
          <fieldset disabled={!changeLogs}>
            <VersionSelector
              branchId={branchId}
              eagerLoad
              onVersionChange={(newVersion) => setSelectedVersion(newVersion.version)}
              version={changelogVersion ?? ''}
            />
          </fieldset>
        </div>
      </div>

      <div>
        {(!changeLogs) ? (
          <Loader loading />
        ) : (
          <div>
            {changeLogs.map((changeMap, i) => {
              const { author, changeLogMap, commitMessage = '', savedFrom, version } = changeMap;
              const source = changeLogMap[0];
              const sourceKeys = Object.keys(source) as (keyof DesignChangeMap)[];

              return (
                <div
                  className="changeLogPanel"
                  key={i}
                >
                  <div className="changeLogPanelHeader">
                    <div>
                      <span className="labels">
                        <i className="fe icon-file-text" />
                        <span>Change Log</span>
                        <i className="fe icon-hash" />
                        <span>{version}</span>
                        <i className="fe icon-user"></i>
                        <span>{author}</span>
                      </span>
                      <div className="commitMessage">{commitMessage}</div>
                    </div>
                    <span className="created">
                      {typeof savedFrom === 'string' && (
                        <span>
                          <span className="savedFrom">
                            Saved from <strong>{savedFrom}</strong>
                          </span>
                        </span>
                      )}
                      <span className="timeStamp">{formatDateTime(changeMap.timeStamp)}</span>
                    </span>
                  </div>

                  <div className="panel">
                    {!sourceKeys.length ? (
                      <h5 className="emptyMessage">No changes in this version</h5>
                    ) : (
                      sourceKeys.map((key, i) => {
                        const filteredMap =
                          (key === 'modules'
                            ? source[key]
                            : key === 'settings'
                              ? source
                              : filterExcluded(source[key])) ?? {};

                        const filteredMapKeys = Object.keys(filteredMap);

                        if (!filteredMapKeys.length) {
                          return null;
                        }

                        return (
                          <div key={i}>
                            <h4>
                              {Utility.getChangeLogLabel(key)} ({filteredMapKeys.length})
                            </h4>
                            <ChangeSetWrapper
                              clKey={key}
                              filteredMap={filteredMap}
                              version={version}
                              serialized={serialized}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
